// /hooks/useResolvePlayerMongoId.js
import { useEffect, useMemo, useRef, useState, useContext } from 'react';
import axiosConn from '../axiosConn';
import { AuthContext } from '../context/AuthContext';

const HEX24 = /^[a-f0-9]{24}$/i;

// simple in-memory cache so we don’t re-fetch the same handle repeatedly
const cache = new Map(); // key: handleOrId -> { mongoId, handle }

export default function useResolvePlayerMongoId(handleOrId) {
  const { player } = useContext(AuthContext);
  const token = player?.token || null;

  const selfHandle = player?.data?.player?.playerId;
  const selfId = player?.data?.player?._id || player?._id || null;

  const initial = useMemo(() => {
    // 1) no param → fall back to logged-in player
    if (!handleOrId) {
      return selfId ? { mongoId: selfId, handle: selfHandle } : null;
    }
    // 2) already a 24-hex string → it's a MongoId
    if (HEX24.test(handleOrId)) return { mongoId: handleOrId, handle: null };
    // 3) param equals our own handle → use our own _id without a fetch
    if (selfHandle && handleOrId === selfHandle && selfId) {
      return { mongoId: selfId, handle: selfHandle };
    }
    // 4) cache hit?
    if (cache.has(handleOrId)) return cache.get(handleOrId);
    return null;
  }, [handleOrId, selfHandle, selfId]);

  const [state, setState] = useState(
    () => initial || { mongoId: null, handle: null },
  );
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    // if we already have an answer, skip fetch
    if (initial) {
      setState(initial);
      setLoading(false);
      setError(null);
      return;
    }
    if (!handleOrId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        // resolve handle → _id
        const res = await axiosConn.get(
          `/players?playerId=${encodeURIComponent(handleOrId)}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          },
        );
        const doc = res?.data?.data?.data?.[0];
        const mongoId = doc?._id || null;
        const handle = doc?.playerId || handleOrId;
        const payload = { mongoId, handle };
        cache.set(handleOrId, payload);
        if (!cancelled && mountedRef.current) {
          setState(payload);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled && mountedRef.current) {
          setError(
            e?.response?.data?.message ||
              e?.message ||
              'Failed to resolve player',
          );
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [handleOrId, initial, token]);

  return { ...state, loading, error };
}
